"use client";
import { buildHref } from "@softmax/web-addresses";
import { SiteNavView } from "@softmax/web-ux/shell";
import { useActualViewer } from "../../session/use-actual-viewer";
import { FeatureLink as Link } from "../../navigation/feature-link";
import { usePathname } from "../../navigation/use-pathname";
import { type AccountAgent, AccountMenu } from "./account-menu";
import { coworldSearchContext, type CoworldNav } from "../coworld-menu";
import { CoworldSwitcher } from "../switcher/coworld-switcher";
import { SearchRow } from "../search/search-row";
import { defaultBrowseLinks, type SiteLink } from "../site-links";
import { navigationLayout } from "./navigation-layout";
import { topnavMode } from "../topnav-mode";
import { navigationDestinations } from "./navigation-destinations";
import { useOnboardingOpen } from "../use-onboarding-open";
import { NavigationReady } from "../../navigation/readiness/navigation-ready";

export function SiteNav({
  browseItems = defaultBrowseLinks,
  coworld,
  signedIn,
  hasRail = false,
  agent = null,
  shellPill = false,
}: {
  readonly browseItems?: SiteLink[] | undefined;
  readonly coworld?: CoworldNav;
  readonly signedIn?: boolean;
  readonly hasRail?: boolean;
  readonly agent?: AccountAgent | null;
  /**
   * The paper shell's account pill replaces this bar wherever everything in
   * it has another home. See `topnavMode`.
   */
  readonly shellPill?: boolean;
}) {
  const clientSignedIn = useActualViewer().data != null;
  const onboardingOpen = useOnboardingOpen();
  const pathname = usePathname();
  const isSignedIn = signedIn ?? clientSignedIn;
  const { railPresent, inProduct, appBar, menus, showSwitcher, showSearch } =
    navigationLayout({
      isSignedIn,
      hasRail,
      coworld,
      onboardingOpen,
      browseItems,
    });
  const { doors, drawerDoors, action } = navigationDestinations({
    coworld,
    appBar,
    isSignedIn,
    pathname,
  });

  const view = (
    <>
      <NavigationReady stage="shell" />
      <SiteNavView
        homeHref={buildHref({ name: inProduct ? "browse" : "home" })}
        brand={{
          markSrc: "/home/nav-brandmark-color.svg",
          wordmarkSrc: "/home/nav-wordmark-black.png",
        }}
        label={coworld ? `${coworld.name} pages` : undefined}
        railPresent={railPresent}
        menus={menus}
        doors={doors}
        drawerDoors={drawerDoors}
        action={action}
        account={
          isSignedIn ? (
            <AccountMenu anchor="masthead" agent={agent} />
          ) : undefined
        }
        switcher={
          showSwitcher ? <CoworldSwitcher coworld={coworld} /> : undefined
        }
        renderSearch={
          showSearch
            ? (onNavigate) => (
                <SearchRow
                  variant="drawer"
                  coworld={coworld ? coworldSearchContext(coworld) : undefined}
                  onNavigate={onNavigate}
                />
              )
            : undefined
        }
        renderLink={(props) => <Link {...props} />}
      />
    </>
  );

  if (!shellPill) return view;

  return (
    <div
      className="shell-topnav"
      data-mode={topnavMode({
        signedIn: isSignedIn,
        railPresent,
        coworld: Boolean(coworld),
      })}
    >
      {view}
    </div>
  );
}
